/**
 * Browser hook for the unified bell. Reads through the app's own
 * `/api/notifications` proxy, and listens on `/api/notifications/stream`: each
 * server-sent "changed" event is a doorbell that triggers a refetch, so the
 * stream never carries notification content.
 *
 * Deliberately dependency-free apart from React, because the apps use
 * different data libraries (TanStack Query, SWR, plain fetch).
 */
import type { InboxBucket } from '@ordo/constants/notification-rules'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  EMPTY_INBOX_COUNTS,
  type InboxCounts,
  type InboxListResponse,
  type InboxNotification,
  type NotificationActionResponse,
  type NotificationDetailResponse,
  type NotificationQuickAction,
  type PushPublicKeyResponse,
} from './contract'

export type InboxTab = 'all' | InboxBucket

export interface UseNotificationsOptions {
  /** The app's proxy mount. Default `/api/notifications`. */
  basePath?: string
  tab?: InboxTab
  /** Skip fetching, e.g. while the popover is closed. Counts still stream. Default true. */
  enabled?: boolean
  fetchImpl?: typeof fetch
}

export interface UseNotificationsResult {
  items: InboxNotification[]
  counts: InboxCounts
  loading: boolean
  /** True when the app could not reach the service. */
  unavailable: boolean
  hasMore: boolean
  loadMore: () => Promise<void>
  refresh: () => Promise<void>
  markRead: (id: string) => Promise<void>
  markUnread: (id: string) => Promise<void>
  /** Clears only the given tab (All clears everything). */
  markAllRead: (tab: InboxTab) => Promise<void>
  /** Stop receiving Following notifications about this item (`POST /mutes`). */
  unfollow: (notification: InboxNotification) => Promise<void>
  /** Undo an unfollow (`DELETE /mutes`). */
  followAgain: (notification: InboxNotification) => Promise<void>
  /**
   * One-click Approve / Decline, run by the owning app as the signed-in person.
   * Resolves with the app's outcome line; throws an `Error` whose message is
   * the app's refusal ("Bond must be in Pending Approval status to approve").
   */
  act: (notification: InboxNotification, action: NotificationQuickAction) => Promise<string>
  /** Posts a reply in the owning app (a comment or note). Throws with the app's refusal. */
  reply: (notification: InboxNotification, text: string) => Promise<void>
  /** The right side, fetched live from the owning app. Never throws: a failure is `unavailable`. */
  loadDetail: (notification: InboxNotification) => Promise<NotificationDetailResponse>
}

const DETAIL_STATUSES: ReadonlySet<string> = new Set(['ready', 'generic', 'deleted', 'no-access', 'unavailable'])

/**
 * The right side's state from the detail endpoint's HTTP answer. Exported for
 * tests. A body naming a known status wins. A bare 404 (a service that has
 * no detail route yet, during a deploy) falls back to the Generic framework,
 * never "deleted". Anything else (5xx, a proxy error, a malformed body) is
 * `unavailable`, which the page offers to retry.
 */
export function detailResponseFrom(httpStatus: number, body: unknown): NotificationDetailResponse {
  const status = (body as { status?: unknown } | null)?.status
  if (typeof status === 'string' && DETAIL_STATUSES.has(status)) {
    if (status !== 'ready') {
      return { status } as NotificationDetailResponse
    }
    const content = (body as { content?: unknown }).content
    if (content && typeof content === 'object' && 'framework' in content) {
      return body as NotificationDetailResponse
    }
    return { status: 'unavailable' }
  }
  return httpStatus === 404 ? { status: 'generic' } : { status: 'unavailable' }
}

/** The refusal to show for a failed action or reply. Exported for tests. */
export function refusalFrom(body: unknown, fallback: string): string {
  const error = (body as { error?: unknown } | null)?.error
  return typeof error === 'string' && error.trim() ? error : fallback
}

/** The body `/mutes` takes for a notification's item: the owning app plus the item's kind and id. */
export function muteBodyFor(notification: Pick<InboxNotification, 'app' | 'item'>): {
  app: string
  itemKind: string
  itemId: string
} {
  return { app: notification.app, itemKind: notification.item.kind, itemId: notification.item.id }
}

/** The list URL for a tab and page. Exported for tests. */
export function inboxListUrl(basePath: string, tab: InboxTab, cursor: string | null): string {
  const params = new URLSearchParams()
  params.set('bucket', tab)
  if (cursor) {
    params.set('cursor', cursor)
  }
  return `${basePath}?${params.toString()}`
}

export function useNotifications(options: UseNotificationsOptions = {}): UseNotificationsResult {
  const basePath = options.basePath ?? '/api/notifications'
  const tab = options.tab ?? 'all'
  const enabled = options.enabled ?? true
  const fetchImpl = options.fetchImpl ?? fetch

  const [items, setItems] = useState<InboxNotification[]>([])
  const [counts, setCounts] = useState<InboxCounts>(EMPTY_INBOX_COUNTS)
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [unavailable, setUnavailable] = useState(false)
  const requestId = useRef(0)

  const refresh = useCallback(async () => {
    const id = ++requestId.current
    setLoading(true)
    try {
      const res = await fetchImpl(enabled ? inboxListUrl(basePath, tab, null) : `${basePath}/counts`, {
        credentials: 'same-origin',
      })
      if (id !== requestId.current) {
        return
      }
      if (!enabled) {
        const next = (await res.json()) as InboxCounts & { unavailable?: boolean }
        setCounts({ assigned: next.assigned, mentioned: next.mentioned, following: next.following, total: next.total })
        setUnavailable(next.unavailable === true)
        return
      }
      const page = (await res.json()) as InboxListResponse
      setItems(page.items)
      setCursor(page.nextCursor)
      setCounts(page.counts)
      setUnavailable(page.unavailable === true)
    } catch {
      if (id === requestId.current) {
        setUnavailable(true)
      }
    } finally {
      if (id === requestId.current) {
        setLoading(false)
      }
    }
  }, [basePath, tab, enabled, fetchImpl])

  const loadMore = useCallback(async () => {
    if (!cursor || !enabled) {
      return
    }
    const res = await fetchImpl(inboxListUrl(basePath, tab, cursor), { credentials: 'same-origin' })
    const page = (await res.json()) as InboxListResponse
    setItems((current) => [...current, ...page.items.filter((item) => !current.some((c) => c.id === item.id))])
    setCursor(page.nextCursor)
    setCounts(page.counts)
  }, [basePath, tab, cursor, enabled, fetchImpl])

  const send = useCallback(
    async (method: 'POST' | 'DELETE', path: string, body?: unknown) => {
      await fetchImpl(`${basePath}${path}`, {
        method,
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body ?? {}),
      })
      await refresh()
    },
    [basePath, fetchImpl, refresh],
  )
  const post = useCallback((path: string, body?: unknown) => send('POST', path, body), [send])

  /** POST and return the parsed body, throwing the app's refusal on a non-OK answer. */
  const call = useCallback(
    async (path: string, body: unknown, fallbackError: string): Promise<unknown> => {
      let res: Response
      try {
        res = await fetchImpl(`${basePath}${path}`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
      } catch {
        throw new Error(fallbackError)
      }
      const parsed: unknown = await res.json().catch(() => null)
      if (!res.ok) {
        throw new Error(refusalFrom(parsed, fallbackError))
      }
      return parsed
    },
    [basePath, fetchImpl],
  )

  const act = useCallback(
    async (notification: InboxNotification, action: NotificationQuickAction): Promise<string> => {
      try {
        const result = (await call(
          `/${encodeURIComponent(notification.id)}/actions/${encodeURIComponent(action)}`,
          {},
          'That didn’t go through. Open it to answer there.',
        )) as NotificationActionResponse | null
        return result?.message ?? ''
      } finally {
        // Either way the row changed (answered, or answered by someone else first); don't hold up the outcome line.
        void refresh()
      }
    },
    [call, refresh],
  )

  const reply = useCallback(
    async (notification: InboxNotification, text: string): Promise<void> => {
      await call(`/${encodeURIComponent(notification.id)}/reply`, { text }, 'Couldn’t send your reply. Try again.')
    },
    [call],
  )

  const loadDetail = useCallback(
    async (notification: InboxNotification): Promise<NotificationDetailResponse> => {
      try {
        const res = await fetchImpl(`${basePath}/${encodeURIComponent(notification.id)}/detail`, {
          credentials: 'same-origin',
        })
        return detailResponseFrom(res.status, await res.json().catch(() => null))
      } catch {
        return { status: 'unavailable' }
      }
    },
    [basePath, fetchImpl],
  )

  useEffect(() => {
    void refresh()
  }, [refresh])

  // Doorbell: one EventSource per mounted bell; any "changed" event refetches.
  useEffect(() => {
    if (typeof EventSource === 'undefined') {
      return
    }
    const source = new EventSource(`${basePath}/stream`)
    const onChanged = () => {
      void refresh()
    }
    source.addEventListener('changed', onChanged)
    return () => {
      source.removeEventListener('changed', onChanged)
      source.close()
    }
  }, [basePath, refresh])

  return {
    items,
    counts,
    loading,
    unavailable,
    hasMore: cursor !== null,
    loadMore,
    refresh,
    markRead: (id) => post(`/${encodeURIComponent(id)}/read`),
    markUnread: (id) => post(`/${encodeURIComponent(id)}/unread`),
    markAllRead: (which) => post('/read-all', { bucket: which }),
    unfollow: (notification) => send('POST', '/mutes', muteBodyFor(notification)),
    followAgain: (notification) => send('DELETE', '/mutes', muteBodyFor(notification)),
    act,
    reply,
    loadDetail,
  }
}

// ── Phone and desktop push ───────────────────────────────────────────────────

export interface UsePushSubscriptionOptions {
  /** The app's proxy mount. Default `/api/notifications`. */
  basePath?: string
  fetchImpl?: typeof fetch
}

export interface UsePushSubscriptionResult {
  /** This browser can do web push here (a service worker plus the Push API). */
  supported: boolean
  /** The service offers push on this app (it answered a VAPID public key). */
  available: boolean
  /** iPhone or iPad in a browser tab: push only works from the Home Screen app (iOS 16.4+). */
  needsHomeScreen: boolean
  permission: NotificationPermission | 'unsupported'
  /** This device holds a subscription made with the service's key. */
  subscribed: boolean
  busy: boolean
  /** Why the last enable or disable failed, in words a person can act on. */
  error: string | null
  subscribe: () => Promise<void>
  unsubscribe: () => Promise<void>
}

/** The JSON body `POST /push-subscriptions` takes, from a browser subscription. Throws if the browser left out a key. */
export function pushSubscriptionBody(subscription: PushSubscriptionJSON): {
  endpoint: string
  keys: { p256dh: string; auth: string }
} {
  const { endpoint, keys } = subscription
  if (!endpoint || !keys?.p256dh || !keys.auth) {
    throw new Error('This browser returned an incomplete push subscription')
  }
  return { endpoint, keys: { p256dh: keys.p256dh, auth: keys.auth } }
}

/** The request that registers this device with the service. Exported for tests. */
export function pushSubscribeRequest(basePath: string, subscription: PushSubscriptionJSON): [string, RequestInit] {
  return [
    `${basePath}/push-subscriptions`,
    {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(pushSubscriptionBody(subscription)),
    },
  ]
}

/** The request that forgets this device. Exported for tests. */
export function pushUnsubscribeRequest(basePath: string, endpoint: string): [string, RequestInit] {
  return [
    `${basePath}/push-subscriptions`,
    {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint }),
    },
  ]
}

/** A VAPID key as the bytes `pushManager.subscribe` wants. */
export function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const raw = atob((base64String + padding).replace(/-/g, '+').replace(/_/g, '/'))
  const bytes = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) {
    bytes[i] = raw.charCodeAt(i)
  }
  return bytes
}

/**
 * Whether a subscription was made with this key. A device subscribed to an
 * app's OWN push (its own VAPID key) is not subscribed to the inbox.
 */
export function sameApplicationServerKey(key: ArrayBuffer | null | undefined, publicKey: string): boolean {
  if (!key) {
    return false
  }
  const a = new Uint8Array(key)
  const b = urlBase64ToUint8Array(publicKey)
  return a.length === b.length && a.every((byte, i) => byte === b[i])
}

/** iPhone or iPad outside the Home Screen app, where Safari has no Push API. */
export function pushNeedsHomeScreen(userAgent: string, standalone: boolean, maxTouchPoints = 0): boolean {
  // iPadOS 13+ reports a Mac user agent; touch points tell them apart.
  const ios = /iPhone|iPad|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1)
  return ios && !standalone
}

function browserPushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  )
}

function browserStandalone(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true
  return iosStandalone || window.matchMedia?.('(display-mode: standalone)').matches === true
}

/**
 * The app's service worker registration. `ready` alone never settles when no
 * worker is registered (local dev, an app without one), so it is only awaited
 * for `waitMs`, covering an app that registers its worker just after mount.
 */
async function currentRegistration(waitMs = 0): Promise<ServiceWorkerRegistration | null> {
  const registered = await navigator.serviceWorker.getRegistration()
  if (registered || waitMs <= 0) {
    return registered ?? null
  }
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), waitMs)),
  ])
}

/**
 * Push for the unified inbox on this device. The VAPID public key comes from
 * the service through the app's proxy (`GET {basePath}/push-public-key`), so
 * no app carries its own copy. Enabling asks for permission, subscribes the
 * app's service worker with that key and registers the subscription with the
 * service; disabling does the reverse.
 *
 * A browser holds ONE push subscription per app. If this device is still
 * subscribed to the app's own push (a different key), enabling replaces it.
 */
export function usePushSubscription(options: UsePushSubscriptionOptions = {}): UsePushSubscriptionResult {
  const basePath = options.basePath ?? '/api/notifications'
  const fetchImpl = options.fetchImpl ?? fetch
  const [browserSupported] = useState(browserPushSupported)
  // An app with no service worker (or none registered yet, as in local dev)
  // has nothing to receive a push with, so the control stays hidden there.
  const [hasWorker, setHasWorker] = useState(false)
  const supported = browserSupported && hasWorker
  const [needsHomeScreen] = useState(() =>
    typeof navigator === 'undefined'
      ? false
      : pushNeedsHomeScreen(navigator.userAgent, browserStandalone(), navigator.maxTouchPoints),
  )
  const [publicKey, setPublicKey] = useState<string | null>(null)
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(() =>
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
  )
  const [subscribed, setSubscribed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const res = await fetchImpl(`${basePath}/push-public-key`, { credentials: 'same-origin' })
        const key = res.ok ? (((await res.json()) as PushPublicKeyResponse).publicKey ?? null) : null
        if (cancelled) {
          return
        }
        setPublicKey(key)
        if (key && browserSupported) {
          const registration = await currentRegistration(5000)
          const existing = await registration?.pushManager.getSubscription()
          if (!cancelled) {
            setHasWorker(registration !== null)
            setSubscribed(sameApplicationServerKey(existing?.options.applicationServerKey, key))
          }
        }
      } catch {
        // No key: the control stays hidden.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [basePath, fetchImpl, browserSupported])

  const subscribe = useCallback(async () => {
    if (!publicKey || !supported) {
      return
    }
    setBusy(true)
    setError(null)
    try {
      const granted = await Notification.requestPermission()
      setPermission(granted)
      if (granted !== 'granted') {
        throw new Error('Notifications are blocked for this site. Allow them in your browser settings, then try again.')
      }
      const registration = await currentRegistration()
      if (!registration) {
        throw new Error('Push needs this app to finish installing. Reload the page and try again.')
      }
      const existing = await registration.pushManager.getSubscription()
      if (existing && !sameApplicationServerKey(existing.options.applicationServerKey, publicKey)) {
        await existing.unsubscribe()
      }
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      })
      const res = await fetchImpl(...pushSubscribeRequest(basePath, subscription.toJSON()))
      if (!res.ok) {
        // The service never stored it, so drop the browser's copy too.
        await subscription.unsubscribe()
        throw new Error('Push could not be turned on right now. Try again shortly.')
      }
      setSubscribed(true)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught))
    } finally {
      setBusy(false)
    }
  }, [basePath, fetchImpl, publicKey, supported])

  const unsubscribe = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      const subscription = await (await currentRegistration())?.pushManager.getSubscription()
      if (subscription) {
        await fetchImpl(...pushUnsubscribeRequest(basePath, subscription.endpoint))
        await subscription.unsubscribe()
      }
      setSubscribed(false)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught))
    } finally {
      setBusy(false)
    }
  }, [basePath, fetchImpl])

  return {
    supported,
    available: publicKey !== null,
    needsHomeScreen,
    permission,
    subscribed,
    busy,
    error,
    subscribe,
    unsubscribe,
  }
}
